import * as React from 'react';

import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import { enumLoadWeightType } from '@rematter/modules/trade/types/load';
import { convertWeight, TouchNumberField } from '@rematter/ui-web';
import Big from 'big.js';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $lastFocusedRowId,
  $lastFocusedWeightFieldType,
} from '../../../../_state/loadStaged.state';
import { useTakeWeightListener } from '../../../../hooks/useScaleReading';
import { useScaleTakeWeight } from '../../../../hooks/useScaleWeight';
import { useWeightConversion } from '../../../../hooks/useWeightConversion';
import { LoadStagedFieldProps } from '../_types';

export const TareWeightField: React.FC<LoadStagedFieldProps> = ({
  lineItem,
  disabled,
  weightUnit = 'price',
  facilityDefaultUnit = enumUnit.Pounds,
  touchModeEnabled,
  onUpdateItem,
  onFocus,
  onBlur,
}) => {
  // Atoms
  const lastFocusedRowId = useAtomValue($lastFocusedRowId);
  const lastFocusedWeightFieldType = useAtomValue($lastFocusedWeightFieldType);
  const setLastFocusedWeightFieldType = useSetAtom($lastFocusedWeightFieldType);

  const { takeWeight } = useScaleTakeWeight();

  const { convertToDisplayUnit, convertFromDisplayUnit, massUnitAbbreviation } =
    useWeightConversion({
      weightUnit,
      facilityDefaultUnit,
      pricingUnit: lineItem.pricingUnit as Unit,
    });

  const storedUnit =
    (lineItem.weights?.[enumLoadWeightType.Tare]?.unit as Unit) ||
    enumUnit.Pounds;

  const handleTareWeightChange = React.useCallback(
    (value: number) => {
      const convertedValue = convertFromDisplayUnit(value, storedUnit);

      onUpdateItem(lineItem.id, {
        weights: {
          ...lineItem.weights,
          [enumLoadWeightType.Tare]: {
            value: convertedValue,
            unit: storedUnit,
            __typename: 'Weight',
          },
          [enumLoadWeightType.Net]: {
            value: new Big(
              lineItem.weights?.[enumLoadWeightType.Gross]?.value ?? 0,
            )
              .minus(convertedValue)
              .toNumber(),
            unit: storedUnit,
            __typename: 'Weight',
          },
        },
      });
    },
    [
      lineItem.weights,
      lineItem.id,
      storedUnit,
      convertFromDisplayUnit,
      onUpdateItem,
    ],
  );

  const applyScaleReading = React.useCallback(
    (scaleValue: number, scaleUnit: Unit) => {
      const convertedValue = convertWeight(scaleValue, scaleUnit, storedUnit);

      onUpdateItem(lineItem.id, {
        weights: {
          ...lineItem.weights,
          [enumLoadWeightType.Tare]: {
            value: convertedValue,
            unit: storedUnit,
            __typename: 'Weight',
          },
          [enumLoadWeightType.Net]: {
            value: new Big(
              lineItem.weights?.[enumLoadWeightType.Gross]?.value ?? 0,
            )
              .minus(convertedValue)
              .toNumber(),
            unit: storedUnit,
            __typename: 'Weight',
          },
        },
      });
    },
    [lineItem.id, lineItem.weights, storedUnit, onUpdateItem],
  );

  useTakeWeightListener(
    ({ weight, unitOfMeasurement }) => {
      if (
        lastFocusedRowId === lineItem.id &&
        lastFocusedWeightFieldType === 'tare'
      ) {
        applyScaleReading(weight, unitOfMeasurement);
      }
    },
    [
      lastFocusedRowId,
      lastFocusedWeightFieldType,
      lineItem.id,
      applyScaleReading,
    ],
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === ' ') {
        e.preventDefault();
        const reading = takeWeight();
        if (reading?.stable) {
          applyScaleReading(
            reading.grossWeight,
            reading.unitOfMeasurement ?? enumUnit.Pounds,
          );
        }
      }
    },
    [takeWeight, applyScaleReading],
  );

  const handleFocus = React.useCallback(() => {
    setLastFocusedWeightFieldType('tare');
    onFocus?.();
  }, [setLastFocusedWeightFieldType, onFocus]);

  const isQuantityBased = lineItem.pricingUnit === enumUnit.Each;

  if (isQuantityBased) {
    return <div />;
  }

  return (
    <TouchNumberField
      aria-label="Tare weight"
      isDisabled={lineItem.type === 'Tagged' || disabled}
      value={convertToDisplayUnit(lineItem.weights?.[enumLoadWeightType.Tare])}
      onChange={handleTareWeightChange}
      onKeyDown={handleKeyDown}
      formatOptions={{ minimumFractionDigits: 0, maximumFractionDigits: 2 }}
      touchModeEnabled={touchModeEnabled}
      variant="outlined"
      suffix={massUnitAbbreviation}
      prefix="T"
      onFocus={handleFocus}
      onBlur={onBlur}
      className="@TareWeightField"
    />
  );
};
